import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, AuthContextType } from '../types';
import { authAPI } from '../services/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(
    localStorage.getItem('sedstart_token')
  );
  const [loading, setLoading] = useState(true);

  const login = async (provider: string, code: string, role?: string) => {
    try {
      let response;
      switch (provider) {
        case 'google':
          response = await authAPI.loginWithGoogle(code, role);
          break;
        default:
          throw new Error('Invalid provider');
      }

      const { token: authToken, user: userData } = response.data;
      setToken(authToken);
      setUser(userData);
      localStorage.setItem('sedstart_token', authToken);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await authAPI.logout();
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setToken(null);
      setUser(null);
      localStorage.removeItem('sedstart_token');
    }
  };

  const updateProfile = async (profileData: Partial<User>) => {
    try {
      await authAPI.updateProfile(profileData);
      if (user) {
        setUser({ ...user, ...profileData, profile_completed: true });
      }
    } catch (error) {
      console.error('Profile update failed:', error);
      throw error;
    }
  };

  useEffect(() => {
    const runValidation = async () => {
      const storedToken = localStorage.getItem('sedstart_token');
      if (!storedToken) {
        setLoading(false);
        return;
      }
      try {
        const response = await authAPI.validateToken();
        if (response.data.valid) {
          const profileResponse = await authAPI.getProfile();
          setUser(profileResponse.data);
        } else {
          setToken(null);
          localStorage.removeItem('sedstart_token');
        }
      } catch (error) {
        console.error('Token validation failed:', error);
        setToken(null);
        localStorage.removeItem('sedstart_token');
      } finally {
        setLoading(false);
      }
    };
    runValidation();
  }, []); // intentionally run once on mount

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    updateProfile,
    isAuthenticated: !!token && !!user,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};